import React, { ComponentProps, useLayoutEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '../../legacy/Box';
import { InlineLink } from '../InlineLink';
import { MarkdownText } from '../MarkdownText';
import { useMaxLines } from './hooks';
import css from './index.module.scss';

export interface ReadMoreMarkdownProps
  extends Pick<ComponentProps<typeof MarkdownText>, 'ignoreMultilineComponents' | 'components'> {
  maxLines: number;
  content: string;
}

// This was modified from an open source project, which also had options like maxCharacters.
// For more info, see this site: https://morioh.com/p/deee1ea6ebf8
export const ReadMoreMarkdown: React.FC<ReadMoreMarkdownProps> = ({
  maxLines,
  content,
  ignoreMultilineComponents,
  components,
}) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [text, setText] = useState<string>('');
  const [rulerText, setRulerText] = useState<string>('');
  const { readMoreRef, textRulerRef, ellipsesRulerRef, truncated } = useMaxLines(
    maxLines,
    isExpanded,
    content,
    setText,
    setRulerText,
  );

  useLayoutEffect(() => {
    setShowButton(truncated || isExpanded);
  }, [isExpanded, truncated]);

  const getButtonLabel = isExpanded
    ? intl.formatMessage({ defaultMessage: 'less', id: 'ah2kTV' })
    : intl.formatMessage({ defaultMessage: 'more', id: 'gk3x/E' });

  const getEllipses = isExpanded ? ' ' : '... ';

  return (
    <Box
      data-component="ReadMoreMarkdown"
      ref={readMoreRef}
      data-testid="wrapper"
      className={css.container}
    >
      <MarkdownText
        text={showButton ? text + getEllipses : text}
        ignoreMultilineComponents={ignoreMultilineComponents}
        components={components}
      />
      <Box style={{ display: showButton ? 'block' : 'none' }}>
        <InlineLink
          type="button"
          onClick={e => {
            setIsExpanded(v => !v);
            e.stopPropagation();
          }}
        >
          {getButtonLabel}
        </InlineLink>
      </Box>
      {/* This "ruler" is used to measure the size of the "..." */}
      <Box
        ref={ellipsesRulerRef}
        className={css.ruler}
        style={{ display: rulerText === '' ? 'none' : 'inline-block' }}
      >
        {getEllipses}
      </Box>
      {/* This "ruler" is used to measure the size of the content text. The hook sets the "rulerText"
      to one word at a time of the content text, checking how long each is to see how many words will
      fill the max number of lines */}
      <Box
        ref={textRulerRef}
        className={css.ruler}
        style={{ display: rulerText === '' ? 'hidden' : 'inline-block' }}
      >
        <MarkdownText
          text={rulerText}
          ignoreMultilineComponents={ignoreMultilineComponents}
          components={components}
        />
      </Box>
    </Box>
  );
};
